import BaseModal from '@/components/modals/BaseModal';
import { useOrganizationUpdaterProvider } from '@/context_providers/organization_context/OrganizationProvider';
import { OrgUser } from '@/models/User/OrgUser';
import { StringKeys } from '@/resources/strings/StringKeys';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import lostConnectionIcon from '@/resources/images/location_notification.png';
import { formatTimeAgo } from '@/utils/time/formatTime';
import {
  getUserStatus,
  useAllDisconnectedUsers,
} from '@/context_providers/group_user_context/OrgGroupUserStatusStore';
import { useMapUiActions, useMapUiStore } from '@/stores/map/mapUiStore';
import { useDashboardActions } from '@/stores/map/dashboardStore';
import useMapToUser from '@/hooks/useMapToUser';
/**
 * @typedef {Object} LostConnectionListModalProps
 */

/**
 *
 * @param {LostConnectionListModalProps} props
 * @returns A modal that displays a list of users who have lost connection.
 *          Allows the user to select a lost connection to see it on the map
 */
const LostConnectionListModal = () => {
  const { t } = useTranslation();
  const { groups } = useOrganizationUpdaterProvider();

  const userIdsWithLostConnections = useAllDisconnectedUsers();

  /**
   * Maps lost connection user IDs to their OrgUsers
   * @type {Array<OrgUser|null>}
   */
  const lostUsers = useMemo(() => {
    if (!userIdsWithLostConnections || !groups || !Array.isArray(groups))
      return [];

    return userIdsWithLostConnections
      .map((userId) => {
        const user = groups
          .flatMap((group) => group.group_value)
          .find((u) => u.auth_id === userId);
        return user ? new OrgUser(user) : null;
      })
      .filter(Boolean);
  }, [groups, userIdsWithLostConnections]);

  const { setShowLostConnectionListModal } = useMapUiActions();
  const { showLostConnectionListModal } = useMapUiStore();
  const dashboardActions = useDashboardActions();
  const { moveMapToUser } = useMapToUser();

  return (
    <BaseModal
      onSubmit={() => {
        setShowLostConnectionListModal(false);
      }}
      open={showLostConnectionListModal}
      submitButtonText={t(StringKeys.CLOSE)}
      onClose={() => setShowLostConnectionListModal(false)}
      title={t(StringKeys.LOST_CONNECTIONS)}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <TableContainer>
        <Table>
          <TableBody>
            {lostUsers.length > 0 ? (
              lostUsers.map((user) => {
                const userStatus = getUserStatus(user.auth_id)?.userStatus;
                return (
                  <TableRow
                    key={user.auth_id}
                    onClick={
                      userStatus?.shouldShowPinOnMap()
                        ? () => {
                            // move map to user location
                            moveMapToUser(user.auth_id);
                            // close the modal
                            setShowLostConnectionListModal(false);
                          }
                        : () => {
                            // Open the user dashboard
                            dashboardActions.openDashboard(user.auth_id);
                            // Close the modal
                            setShowLostConnectionListModal(false);
                          }
                    }
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  >
                    <TableCell>
                      <img
                        src={lostConnectionIcon}
                        alt={'Lost Connection Icon'}
                        style={{
                          width: '50px',
                          height: '50px',
                        }}
                      />
                    </TableCell>
                    <TableCell>Lost Connection</TableCell>
                    <TableCell>
                      {user.first_name} {user.last_name}
                    </TableCell>
                    <TableCell>
                      {formatTimeAgo(userStatus?.dto.timestamp * 1000) ?? 'N/A'}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={4}>
                  {t(StringKeys.NO_LOST_CONNECTIONS)}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </BaseModal>
  );
};

export default LostConnectionListModal;
